import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ProjectDtoRelV2 } from '../../../commonTypes/project.types';
import { StatusColumn } from './StatusColumn';

interface ProjectsGridViewProps {
    projects: ProjectDtoRelV2[];
    onUpdateProjectStatus: (projectId: string, newStatus: string) => void;
    searchTerm: string;
    selectedFilters: string[];
}

interface StatusConfig {
    value: string;
    label: string;
    color: string;
}

const STATUS_CONFIGS: StatusConfig[] = [
    { value: 'KickOff', label: 'Kick Off', color: '#AF52DE' },
    { value: 'InProgress', label: 'In Progress', color: '#4B99F5' },
    { value: 'OnHold', label: 'On Hold', color: '#E5A000' },
    { value: 'Cancelled', label: 'Cancelled', color: '#EB6060' },
    { value: 'Completed', label: 'Completed', color: '#14AE5C' },
];

export const ProjectsGridView = ({ projects, onUpdateProjectStatus, searchTerm, selectedFilters }: ProjectsGridViewProps) => {
    const navigate = useNavigate();
    const [draggedProjectId, setDraggedProjectId] = useState<string | null>(null);

    const filtered = useMemo(() => {
        const q = searchTerm.toLowerCase().trim();

        let result = !q
            ? [...projects]
            : projects.filter(p => {
                  const n = p.name?.toLowerCase() ?? '';
                  const c = p.clientName?.toLowerCase() ?? '';
                  return n.includes(q) || c.includes(q);
              });

        if (selectedFilters.length > 0) {
            result = result.filter(project => {
                const projectStatus = project.projectStatus || (project.active ? 'InProgress' : 'Draft');
                return selectedFilters.includes(projectStatus);
            });
        }

        return result;
    }, [projects, searchTerm, selectedFilters]);

    const projectsByStatus = useMemo(() => {
        const grouped: Record<string, ProjectDtoRelV2[]> = {};

        STATUS_CONFIGS.forEach(config => {
            grouped[config.value] = [];
        });

        filtered.forEach(project => {
            const status = project.projectStatus || (project.active ? 'InProgress' : 'Draft');
            if (grouped[status]) {
                grouped[status].push(project);
            } else {
                grouped['InProgress'].push(project);
            }
        });

        return grouped;
    }, [filtered]);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, projectId: string) => {
        setDraggedProjectId(projectId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', projectId);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, newStatus: string) => {
        e.preventDefault();

        if (draggedProjectId) {
            const project = projects.find(p => p.id === draggedProjectId);
            const currentStatus = project?.projectStatus || (project?.active ? 'InProgress' : 'Draft');

            if (currentStatus !== newStatus) {
                onUpdateProjectStatus(draggedProjectId, newStatus);
            }
        }

        setDraggedProjectId(null);
    };

    const handleViewDetails = (projectId: string) => {
        navigate(`/project/${projectId}`);
    };

    return (
        <div className="projects-grid-view">
            <div className="projects-grid-view__container">
                {STATUS_CONFIGS.map(config => (
                    <StatusColumn
                        key={config.value}
                        status={config.value}
                        label={config.label}
                        color={config.color}
                        projects={projectsByStatus[config.value] || []}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onViewDetails={handleViewDetails}
                    />
                ))}
            </div>
        </div>
    );
};
